import React, { useState } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { Checkbox } from './Checkbox';
import { Info, Check, ChevronDown, Plus, X, Image as ImageIcon, ArrowLeft, ArrowRight } from 'lucide-react';
export function FlowPanel({
  onBack,
  onNext
}: {
  onBack?: () => void;
  onNext?: () => void;
}) {
  const [layoutMode, setLayoutMode] = useState<'single' | 'wizard'>('wizard');
  const [transition, setTransition] = useState('slide-horizontal');
  const [transitionSpeed, setTransitionSpeed] = useState('normal');
  const [customSpeed, setCustomSpeed] = useState('250');
  const [progressIndicator, setProgressIndicator] = useState('step-bar-labels');
  const [progressBarOptions, setProgressBarOptions] = useState({
    showStepLabels: true,
    showSummaryValues: true,
    showConnectingLines: false,
    showRunningTotal: false
  });
  const [progressBarCss, setProgressBarCss] = useState(`.widget-progress-bar { }\n.widget-progress-bar .step-dot { }\n.widget-progress-bar .step-dot.active { }\n.widget-progress-bar .step-line { }`);
  const [widgetTitle, setWidgetTitle] = useState('Ultimate Winery Plan');
  const [widgetDescription, setWidgetDescription] = useState('A wine club by Oak Valley Estate. Create your custom plan by selecting your preferences below.');
  const [valuePropositions, setValuePropositions] = useState(['20% added value in every box', '20% off all online purchases, always']);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [stepContent, setStepContent] = useState<Record<number, {
    progressLabel: string;
    summaryFormat: string;
    headingOverride: string;
    description: string;
    css: string;
  }>>({
    1: {
      progressLabel: 'HOW MANY?',
      summaryFormat: '{pack_size}',
      headingOverride: 'How many bottles would you like?',
      description: 'Select your preferred pack size. Larger packs offer better value per bottle.',
      css: '/* .step--pack-size { } */'
    },
    2: {
      progressLabel: 'HOW OFTEN?',
      summaryFormat: '{frequency}',
      headingOverride: 'How often would you like your plan delivered?',
      description: 'Choose your preferred delivery schedule. You can skip, pause or change frequency at any time from your account.',
      css: '/* .step--frequency { } */'
    },
    3: {
      progressLabel: 'YOUR WINES',
      summaryFormat: '{count} selected',
      headingOverride: 'Choose your wines',
      description: "Browse our collection and pick your favourites. We'll pack them fresh for your next shipment.",
      css: '/* .step--products { } */'
    },
    4: {
      progressLabel: 'REVIEW',
      summaryFormat: '{total}',
      headingOverride: 'Review your order',
      description: 'Check your selections, pricing, and frequency before adding to cart.',
      css: '/* .step--review { } */'
    }
  });
  const toggleStepContent = (stepNumber: number) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepNumber)) {
        next.delete(stepNumber);
      } else {
        next.add(stepNumber);
      }
      return next;
    });
  };
  const updateStepContent = (stepNumber: number, field: string, value: string) => {
    setStepContent(prev => ({
      ...prev,
      [stepNumber]: {
        ...prev[stepNumber],
        [field]: value
      }
    }));
  };
  const steps = [{
    number: 1,
    title: 'Choose Pack Size',
    description: 'Determines available frequencies, product pool, and pricing'
  }, {
    number: 2,
    title: 'Choose Frequency',
    description: 'Sets discount percentage shown on product cards and in the summary'
  }, {
    number: 3,
    title: 'Choose Products',
    description: "Product grid filtered to the pack size's collection. Curated boxes show a read-only display."
  }, {
    number: 4,
    title: 'Review & Add to Cart',
    description: 'Summary of selected wines, pricing, frequency, and checkout action'
  }];
  return <div className="flex flex-col gap-6 max-w-4xl mx-auto" data-id="element-3104" data-mp="p5gbr2l4glz0">
      {/* Section 1: Layout Mode */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3105" data-mp="14jaltbtpwrgx">
        <div className="p-6" data-id="element-3106" data-mp="zpxzj8cigwrc">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3107" data-mp="zowkkssdjq4c">
            Layout Mode
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3108" data-mp="qq1gbm8w4q3o">
            How the signup experience is presented to the customer on the
            storefront
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-3109" data-mp="1nfiruhuk16da">
            <div onClick={() => setLayoutMode('single')} className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col items-center text-center ${layoutMode === 'single' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-3110" data-mp="1jfrwe0u3222f">
              {layoutMode === 'single' ? <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#0EA5E9] flex items-center justify-center" data-id="element-3111" data-mp="31rz6xmmgqiv">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-3112" data-mp="3k2uun678l6o1" />
                </div> : <div className="absolute top-4 right-4 w-6 h-6 rounded-full border-2 border-[#CBD5E1]" data-id="element-3113" data-mp="3k0qiy233j3d7" />}
              <div className="w-full aspect-[16/9] bg-[#F1F5F9] rounded-lg mb-6 p-4 flex flex-col items-center justify-center gap-2" data-id="element-3114" data-mp="3ozl6tifx7i46">
                <div className="w-1/2 h-3 bg-[#CBD5E1] rounded-full mb-2" data-id="element-3115" data-mp="2mlbfk3zks36" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-3116" data-mp="6nyi1wewkzb5" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-3117" data-mp="6nondviwy3z4" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-3118" data-mp="blq9kpbmyjet" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-3119" data-mp="gll18v38jd0x" />
              </div>
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-2" data-id="element-3120" data-mp="lkxk74qzjzy8">
                Single Page
              </h3>
              <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-3121" data-mp="2ie93b1457uf4">
                All steps visible in one scrollable page. Fast for simple boxes.
              </p>
            </div>
            <div onClick={() => setLayoutMode('wizard')} className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col items-center text-center ${layoutMode === 'wizard' ? 'border-[#0EA5E9] bg-[#E0F2FE]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-3122" data-mp="2fbcvk2bq0sry">
              {layoutMode === 'wizard' ? <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#0EA5E9] flex items-center justify-center" data-id="element-3123" data-mp="2ka64dhg2bvls">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-3124" data-mp="2y8npd7el2u00" />
                </div> : <div className="absolute top-4 right-4 w-6 h-6 rounded-full border-2 border-[#CBD5E1]" data-id="element-3125" data-mp="2tbdsx7ss1i2o" />}
              <div className="w-full aspect-[16/9] bg-[#E0F2FE] rounded-lg mb-6 flex items-end justify-center gap-2 px-8 pb-4" data-id="element-3126" data-mp="37zs3k4wfqats">
                <div className="w-4 h-8 bg-[#BFDBFE] rounded-t-sm" data-id="element-3127" data-mp="3h187btsqvep1" />
                <div className="w-4 h-12 bg-[#BFDBFE] rounded-t-sm" data-id="element-3128" data-mp="3h1hvrcy55dsm" />
                <div className="w-4 h-16 bg-[#3B82F6] rounded-t-sm" data-id="element-3129" data-mp="3d0o88kom2109" />
                <div className="w-4 h-10 bg-[#BFDBFE] rounded-t-sm" data-id="element-3130" data-mp="3vo6x7kbye11n" />
              </div>
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-2" data-id="element-3131" data-mp="4hnp4cxi4p71">
                Multi-Step Wizard
              </h3>
              <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-3132" data-mp="3mo2146c7mp2s">
                Guided step-by-step flow with progress bar. Focused experience,
                one decision at a time.
              </p>
            </div>
          </div>
          <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-3133" data-mp="3cvcoh84xb55v">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-3134" data-mp="3hpgxry1nvpa4" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-3135" data-mp="33y2cfkka7bzw">
              <span className="font-semibold text-[#0F172A]" data-id="element-3136" data-mp="37xuqgcsg2acn">Multi-step</span>{' '}
              shows a progress bar and Next/Back buttons. Each block becomes its
              own step.{' '}
              <span className="font-semibold text-[#0F172A]" data-id="element-3137" data-mp="2yy7mpkc8ssgf">Single page</span>{' '}
              stacks all blocks vertically with anchor navigation. Both use the
              same Theme App Extension blocks — agencies can override styling
              either way.
            </p>
          </div>
          {layoutMode === 'wizard' && <div className="mt-6 border border-[#E2E8F0] rounded-xl p-6" data-id="element-3138" data-mp="35dyd72teyb96">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3139" data-mp="30gfz6y0jh5ub">
                <div data-id="element-3140" data-mp="30i968ef2ds2u">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3141" data-mp="2vgjyb7n51uiz">
                    Transition
                  </label>
                  <div className="relative" data-id="element-3142" data-mp="2qok3ftlxdrt9">
                    <select value={transition} onChange={e => setTransition(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3143" data-mp="2lqquist8mjpa">
                      <option value="slide-horizontal" data-id="element-3144" data-mp="27qzaaac4o72u">Slide horizontal</option>
                      <option value="slide-vertical" data-id="element-3145" data-mp="n9go9wzvomtj">Slide vertical</option>
                      <option value="fade" data-id="element-3146" data-mp="nbb2ksgn9gon">Fade</option>
                      <option value="instant" data-id="element-3147" data-mp="s78i4wk32j0d">Instant (no animation)</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3148" data-mp="x8pgakb6nrd9" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3149" data-mp="4ip4e1i9le0w">
                    How the widget animates between steps
                  </p>
                </div>
                <div data-id="element-3150" data-mp="9i1wb0nztbac">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3151" data-mp="djezf2zpb0dk">
                    Transition Speed
                  </label>
                  <div className="relative" data-id="element-3152" data-mp="iax0rs5mz2z2">
                    <select value={transitionSpeed} onChange={e => setTransitionSpeed(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3153" data-mp="3hxljpj1zfzhh">
                      <option value="slow" data-id="element-3154" data-mp="115csvi4pnz7o">Slow (500ms)</option>
                      <option value="normal" data-id="element-3155" data-mp="117z1ll3c8krh">Normal (300ms)</option>
                      <option value="fast" data-id="element-3156" data-mp="15892wkg6qn00">Fast (150ms)</option>
                      <option value="custom" data-id="element-3157" data-mp="1a7c7k3s96r7g">Custom</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3158" data-mp="dgthgtqd76bd" />
                  </div>
                  {transitionSpeed === 'custom' && <div className="mt-2 flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-3159" data-mp="mh890atx9hnb">
                      <input type="number" value={customSpeed} onChange={e => setCustomSpeed(e.target.value)} min="0" max="5000" step="50" className="flex-1 px-3 py-2 text-[14px] text-[#0F172A] focus:outline-none w-16 text-center font-medium" data-id="element-3160" data-mp="r9t197x6jmoq" />
                      <div className="bg-[#F8FAFC] px-4 py-2 text-[14px] text-[#64748B] border-l border-[#CBD5E1] flex items-center" data-id="element-3161" data-mp="3nchass6nlem3">
                        ms
                      </div>
                    </div>}
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3162" data-mp="3de1gyhg8rr8w">
                    Animation duration. Ignored when transition is set to
                    Instant.
                  </p>
                </div>
                <div data-id="element-3163" data-mp="34fzbnpph6cft">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3164" data-mp="34ghifg9rk91z">
                    Progress Indicator
                  </label>
                  <div className="relative" data-id="element-3165" data-mp="2upmtu591oxdd">
                    <select value={progressIndicator} onChange={e => setProgressIndicator(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3166" data-mp="2umikktgq3bfu">
                      <option value="step-bar-labels" data-id="element-3167" data-mp="270yaz4gb340v">
                        Step bar with labels
                      </option>
                      <option value="step-bar-no-labels" data-id="element-3168" data-mp="2b4mlnw08ifqc">
                        Step bar without labels
                      </option>
                      <option value="dots" data-id="element-3169" data-mp="1x5mtwfhfdkpy">Dots</option>
                      <option value="fraction" data-id="element-3170" data-mp="2236b9o9f6dm4">Fraction (1 of 4)</option>
                      <option value="none" data-id="element-3171" data-mp="2prpis1knamd5">None</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-3172" data-mp="2uqso7n4jeuln" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3173" data-mp="2g1w770ki678w">
                    How the customer sees their progress through the flow
                  </p>
                </div>
              </div>
              <div className="mt-6 border-t border-dashed border-[#E2E8F0] pt-6" data-id="element-3174" data-mp="38pin98ax34cn">
                <label className="block text-[12px] font-semibold text-[#0EA5E9] uppercase tracking-wider mb-4" data-id="element-3175" data-mp="3dj4pe6jgfpu7">
                  Progress Bar Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3176" data-mp="x49p59ptdzoe">
                  {[{
                key: 'showStepLabels',
                label: 'Show step labels',
                desc: 'Display labels like "How many?", "How often?" above the progress dots'
              }, {
                key: 'showSummaryValues',
                label: 'Show step summary values',
                desc: 'Display current selection under each label (e.g. "Three Wines", "$99/mo")'
              }, {
                key: 'showConnectingLines',
                label: 'Show connecting lines',
                desc: 'Lines connecting the step dots to show progression'
              }, {
                key: 'showRunningTotal',
                label: 'Show running total',
                desc: 'Display a running order total in the last progress step'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} className={`rounded-lg p-4 flex gap-3 border ${progressBarOptions[key as keyof typeof progressBarOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white'}`} data-id="element-3177" data-mp="2twqigjtt6u5n">
                      <div className="pt-0.5" data-id="element-3178" data-mp="33v6b4r8hibh9">
                        <Checkbox checked={progressBarOptions[key as keyof typeof progressBarOptions]} onChange={checked => setProgressBarOptions(prev => ({
                    ...prev,
                    [key]: checked
                  }))} data-id="element-3179" data-mp="38sy0pelnplia" />
                      </div>
                      <div data-id="element-3180" data-mp="3mjl1dcaz9lh0">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3181" data-mp="3mkcmft6y80t1">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-3182" data-mp="3rk8qfsfnbs8l">{desc}</div>
                      </div>
                    </div>)}
                </div>
                <div className="mt-6" data-id="element-3183" data-mp="4eq3p3yd7mke">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-3184" data-mp="4blprev3lcgj">
                    Progress Bar CSS
                  </label>
                  <textarea value={progressBarCss} onChange={e => setProgressBarCss(e.target.value)} className="w-full h-[120px] px-4 py-3 bg-[#0F172A] border border-[#334155] rounded-[6px] text-[13px] text-[#94A3B8] font-mono focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y leading-relaxed" data-id="element-3185" data-mp="1kt2mkiistvrg" />
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3186" data-mp="28lswynb206zm">
                    CSS scoped to{' '}
                    <code className="font-mono text-[#64748B]" data-id="element-3187" data-mp="23mziark169xv">
                      .widget-progress-bar
                    </code>
                    . Override dot size, colours, icons, connecting line styles.
                    Per-step labels are configured in the Signup Flow card
                    below.
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </Card>

      {/* Section 2: Widget Header */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3188" data-mp="kn8mzs5jzr0f">
        <div className="p-6" data-id="element-3189" data-mp="onapu4n0yqha">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3190" data-mp="omhj33o2g0z5">
            Widget Header
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-3191" data-mp="avmfa5sfca2f">
            Static content shown above the signup steps — visible throughout the
            entire flow
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-3192" data-mp="www89oskjkl">
            <div data-id="element-3193" data-mp="5wt26eh1ul2s">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-3194" data-mp="5xb49rqe7tim">
                Widget Title
              </label>
              <input type="text" value={widgetTitle} onChange={e => setWidgetTitle(e.target.value)} placeholder="e.g. Ultimate Winery Plan" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3195" data-mp="3o4ih65hdpool" />
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3196" data-mp="21dj98ibza8a6">
                Main heading displayed above the progress bar. Leave blank to
                hide.
              </p>
            </div>
            <div data-id="element-3197" data-mp="2a6w8s34js858">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-3198" data-mp="2a7ftfrll1amy">
                Header Image
              </label>
              <div className="flex items-center gap-4" data-id="element-3199" data-mp="25e34thgwl3u7">
                <div className="w-14 h-14 rounded-lg border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0 cursor-pointer hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" data-id="element-3200" data-mp="2k3r3sc1m3qer">
                  <ImageIcon className="w-6 h-6" data-id="element-3201" data-mp="2t3e8b6pn1cc1" />
                </div>
                <div data-id="element-3202" data-mp="2p585g9rx7gwe">
                  <p className="text-[13px] text-[#475569]" data-id="element-3203" data-mp="2p1spl4qfa3tx">
                    <span className="font-bold text-[#0F172A]" data-id="element-3204" data-mp="ysio1753c8tf">
                      Upload image
                    </span>{' '}
                    or drag and drop
                  </p>
                  <p className="text-[12px] text-[#94A3B8] mt-0.5" data-id="element-3205" data-mp="1jblw0g1l2lkb">
                    Hero banner displayed above the title.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-3206" data-mp="1t1x0q4t3cys9">
            <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-3207" data-mp="1t2zquailax74">
              Widget Description
            </label>
            <textarea value={widgetDescription} onChange={e => setWidgetDescription(e.target.value)} placeholder="Describe your wine club to customers..." className="w-full h-[100px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-3208" data-mp="10chtzr8gls25" />
            <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3209" data-mp="2ks1qyk3j8g1e">
              Supporting copy shown below the widget title
            </p>
          </div>
          <div className="mb-6" data-id="element-3210" data-mp="2pp1p88u54ehy">
            <label className="block text-[14px] font-semibold text-[#0F172A] mb-1" data-id="element-3211" data-mp="2zutg9n3ea5mw">
              Value Propositions
            </label>
            <p className="text-[12px] text-[#94A3B8] mb-3" data-id="element-3212" data-mp="2uwrq6i7jx61x">
              Short benefit statements shown as pills between the description
              and the first step
            </p>
            <div className="space-y-3" data-id="element-3213" data-mp="2m47y60lrlzq2">
              {valuePropositions.map((prop, index) => <div key={index} className="flex items-center gap-3 border border-[#E2E8F0] rounded-lg bg-white" data-id="element-3214" data-mp="2h423ebk77x3r" data-mpi={index}>
                  <input type="text" value={prop} onChange={e => {
                const updated = [...valuePropositions];
                updated[index] = e.target.value;
                setValuePropositions(updated);
              }} className="flex-1 h-[44px] px-4 text-[14px] text-[#0F172A] bg-transparent focus:outline-none" data-id="element-3215" data-mp="2c4z6xfiaaz7e" />
                  <button onClick={() => setValuePropositions(prev => prev.filter((_, i) => i !== index))} className="p-2 mr-2 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" data-id="element-3216" data-mp="5e97djj7efy7">
                    <X className="w-4 h-4" data-id="element-3217" data-mp="243l9gx5tgchc" />
                  </button>
                </div>)}
              <button onClick={() => setValuePropositions(prev => [...prev, ''])} className="w-full py-3 border-2 border-dashed border-[#CBD5E1] rounded-lg flex items-center justify-center gap-2 text-[13px] font-medium text-[#0EA5E9] hover:border-[#0EA5E9] hover:bg-[#F0F9FF] transition-colors" data-id="element-3218" data-mp="203b49mpulq3j">
                <Plus className="w-4 h-4" data-id="element-3219" data-mp="1lfgcc8jnzpri" />
                Add Value Prop
              </button>
            </div>
          </div>
          <div className="border border-[#E2E8F0] rounded-xl bg-[#F8FAFC] p-8 text-center" data-id="element-3220" data-mp="1gh52hxtddf9a">
            {widgetTitle && <h3 className="text-[20px] font-bold text-[#0F172A] mb-2" data-id="element-3221" data-mp="17e3zw73pq6ad">
                {widgetTitle}
              </h3>}
            {widgetDescription && <p className="text-[14px] text-[#94A3B8] mb-4 max-w-xl mx-auto" data-id="element-3222" data-mp="36pwyg865fjp7">
                {widgetDescription}
              </p>}
            {valuePropositions.filter(v => v.trim()).length > 0 && <div className="flex items-center justify-center gap-3 flex-wrap" data-id="element-3223" data-mp="39cfpf7fdoapo">
                {valuePropositions.filter(v => v.trim()).map((prop, index) => <span key={index} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E2E8F0] bg-white text-[13px] text-[#475569]" data-id="element-3224" data-mp="3e8o12aud9ls3" data-mpi={index}>
                      <Check className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-3225" data-mp="3nz8z9x64ooah" />
                      {prop}
                    </span>)}
              </div>}
          </div>
        </div>
      </Card>

      {/* Section 3: Signup Flow */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-3226" data-mp="2vc522eda1e99">
        <div className="p-6" data-id="element-3227" data-mp="30apys3tblfsy">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3228" data-mp="309odl6ilbcxi">
            Signup Flow
          </h2>
          <p className="text-[13px] text-[#64748B] mb-8" data-id="element-3229" data-mp="34agpk7iujvxh">
            The core steps run in a fixed order driven by the data model.
            Optional blocks can be toggled on and positioned between steps.
          </p>
          <div className="space-y-8 relative" data-id="element-3230" data-mp="2gmygaagy6syu">
            <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-[#E2E8F0]" data-id="element-3231" data-mp="2glnbq8fohh6o" />
            {steps.map(step => {
            const isExpanded = expandedSteps.has(step.number);
            const content = stepContent[step.number];
            return <div key={step.number} className="relative" data-id="element-3232" data-mp="2kok24lbgmhl6">
                  <div className="flex gap-4" data-id="element-3233" data-mp="2kp39x97ebplh">
                    <div className="relative z-10 w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0 text-[#0EA5E9] font-bold text-[14px]" data-id="element-3234" data-mp="1wyppo4v1yv1g">
                      {step.number}
                    </div>
                    <div className="flex-1 pt-1" data-id="element-3235" data-mp="26pu33052y7u6">
                      <div className="flex items-start justify-between gap-4" data-id="element-3236" data-mp="26qn5xq0x4d71">
                        <div data-id="element-3237" data-mp="21wr5mgx6me8p">
                          <div className="flex items-center gap-2 mb-1" data-id="element-3238" data-mp="1e880ga9fihku">
                            <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-3239" data-mp="1e6epwjvp3d7k">
                              {step.title}
                            </h3>
                            <button onClick={() => toggleStepContent(step.number)} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${isExpanded ? 'bg-[#E0F2FE] text-[#0284C7]' : 'bg-[#E0F2FE] text-[#0EA5E9] hover:bg-[#BFDBFE]'}`} data-id="element-3240" data-mp="3kvp2r3qrnfzm">
                              <Plus className="w-3 h-3" data-id="element-3241" data-mp="3gw7y2cj5ymvi" />
                              Content
                            </button>
                          </div>
                          <p className="text-[13px] text-[#94A3B8]" data-id="element-3242" data-mp="cgonjacmk1tb">
                            {step.description}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[11px] font-bold bg-[#F1F5F9] text-[#64748B] uppercase tracking-wider border border-[#E2E8F0]" data-id="element-3243" data-mp="3uonu2tdndkmx">
                          Fixed
                        </span>
                      </div>
                      {isExpanded && content && <div className="mt-4 border border-[#E2E8F0] rounded-xl bg-[#F8FAFC] p-5 space-y-5" data-id="element-3244" data-mp="2o44s8ylz1w95">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-3245" data-mp="2ypfpwe7m9cnu">
                            <div data-id="element-3246" data-mp="2ynbdf7vnj5by">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-3247" data-mp="2unaylm4n4s4d">
                                Progress Label
                              </label>
                              <input type="text" value={content.progressLabel} onChange={e => updateStepContent(step.number, 'progressLabel', e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3248" data-mp="3meqfczjinkmo" />
                              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3249" data-mp="38epe3izcab6o">
                                Text shown above the progress dot for this step
                              </p>
                            </div>
                            <div data-id="element-3250" data-mp="1x2ev24aedtff">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-3251" data-mp="1mwwtmcia07fn">
                                Progress Summary Format
                              </label>
                              <input type="text" value={content.summaryFormat} onChange={e => updateStepContent(step.number, 'summaryFormat', e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3252" data-mp="1wtruh220ujx3" />
                              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3253" data-mp="2anst7vsz46xg">
                                Dynamic value shown below the label.
                              </p>
                            </div>
                          </div>
                          <div data-id="element-3254" data-mp="l7bcilx8sgul">
                            <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-3255" data-mp="l8ckghiyon83">
                              Step Heading Override
                            </label>
                            <input type="text" value={content.headingOverride} onChange={e => updateStepContent(step.number, 'headingOverride', e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3256" data-mp="i5ydmso5cnz6" />
                            <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-3257" data-mp="114147bwcesz3">
                              Replaces the default "{step.title}" heading on
                              this step
                            </p>
                          </div>
                          <div data-id="element-3258" data-mp="1atb1d661n1u2">
                            <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-3259" data-mp="15urcaww5l9xh">
                              Step Description
                            </label>
                            <textarea value={content.description} onChange={e => updateStepContent(step.number, 'description', e.target.value)} className="w-full h-[80px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-3260" data-mp="1jsfz4a9s6r56" />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-3261" data-mp="1pg1ed4t4qivh">
                            <div data-id="element-3262" data-mp="1ueb6f6jk53cv">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-3263" data-mp="1uhqkq75eq8d8">
                                Step Image
                              </label>
                              <div className="flex items-center gap-3" data-id="element-3264" data-mp="1kireswc10qtx">
                                <div className="w-12 h-12 rounded-lg border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0 cursor-pointer hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" data-id="element-3265" data-mp="16r35f0t5wggn">
                                  <ImageIcon className="w-5 h-5" data-id="element-3266" data-mp="11qyvjtmrejze" />
                                </div>
                                <div data-id="element-3267" data-mp="1h8ztan3tcldv">
                                  <p className="text-[13px] font-semibold text-[#0F172A]" data-id="element-3268" data-mp="1h7x6ordix53r">
                                    Upload image
                                  </p>
                                  <p className="text-[12px] text-[#94A3B8]" data-id="element-3269" data-mp="187s896qb5h1q">
                                    Banner above or beside the step
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div data-id="element-3270" data-mp="tjnnlx7p2vgb">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-3271" data-mp="tim0uvhbgkir">
                                Step CSS
                              </label>
                              <textarea value={content.css} onChange={e => updateStepContent(step.number, 'css', e.target.value)} className="w-full h-[60px] px-4 py-3 bg-[#0F172A] border border-[#334155] rounded-[6px] text-[13px] text-[#94A3B8] font-mono focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-3272" data-mp="kjiijtpz495b" />
                            </div>
                          </div>
                        </div>}
                    </div>
                  </div>
                </div>;
          })}
          </div>
          <div className="mt-8 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-3273" data-mp="2w6ucisywu167">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-3274" data-mp="306v31bzaa2tm" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-3275" data-mp="23igdqazzz0tl">
              This order can't be changed because each step depends on the
              previous one. Pack size determines which frequencies and products
              are available, and frequency sets the discount shown during
              product selection.
            </p>
          </div>
        </div>
      </Card>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-2 pb-4" data-id="element-3276" data-mp="1kqnkqgkxhflv">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-3277" data-mp="1orfurc1mjoj7">
          <ArrowLeft className="w-4 h-4" data-id="element-3278" data-mp="13gvlg2tyhekq" />
          Membership
        </button>
        <button onClick={onNext} className="flex items-center gap-2 px-5 py-2 text-[14px] font-medium text-white bg-[#0EA5E9] hover:bg-[#0284C7] rounded-[6px] transition-colors" data-id="element-3279" data-mp="ph3znfx76tw6">
          Next: Theming
          <ArrowRight className="w-4 h-4" data-id="element-3280" data-mp="frar6nscquw5" />
        </button>
      </div>
    </div>;
}